// 每首歌的「起始位置」（Cue 庫專案）：這首歌的第一個 cue 從哪裡開始走過去。
//   prev         接上一首的結尾（預設）：照歌單順序，上一首最後一個 cue 的機關位置；第一首沒有上一首 → 機關預設位置
//   library_cue  指定一個 Cue 庫的 cue 的機關位置（指定的被刪了 → 退回 prev）
//   defaults     機關的預設位置
// 後台（cf-worker/songStart.mjs）算好 start 一起回在 mechanism-positions；管理頁、導演頁、工作站、分享頁、3D 播放、時間軸都用同一份。
// 舊模式（非 Cue 庫）專案沒有 start → 維持原樣（第一個 cue 只當起始值）。

import type { MechanismValues } from './model';

export type StartMode = 'prev' | 'library_cue' | 'defaults';

export type StartSource =
  | { kind: 'prev_song'; songId: string | null; songName: string | null; songCode: string | null }
  | { kind: 'library_cue'; cueId: string | null; cueLabel: string; cueName: string }
  | { kind: 'defaults'; reason: 'chosen' | 'first_song' | 'missing_cue' };

export interface SongStart {
  /** 實際生效的 */
  mode: StartMode;
  /** 這首歌選的（指定的 cue 被刪了 → mode 是 prev、requestedMode 還是 library_cue） */
  requestedMode: StartMode;
  source: StartSource;
  /** 有位置的機關（其他在預設位置） */
  values: MechanismValues;
}

const MODES: StartMode[] = ['prev', 'library_cue', 'defaults'];
const asMode = (v: unknown): StartMode => (MODES.includes(v as StartMode) ? (v as StartMode) : 'prev');
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** 後台回的 start → 檢查過的（舊後台沒有 / 壞掉 → undefined = 沒有起始位置，維持原樣） */
export function parseSongStart(raw: unknown): SongStart | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const s = (r.source && typeof r.source === 'object' ? r.source : {}) as Record<string, unknown>;
  const values: MechanismValues = {};
  if (r.values && typeof r.values === 'object' && !Array.isArray(r.values)) {
    for (const [id, v] of Object.entries(r.values as Record<string, unknown>)) if (typeof v === 'number' && Number.isFinite(v)) values[id] = v;
  }
  let source: StartSource;
  if (s.kind === 'prev_song') source = { kind: 'prev_song', songId: str(s.songId), songName: str(s.songName), songCode: str(s.songCode) };
  else if (s.kind === 'library_cue') source = { kind: 'library_cue', cueId: str(s.cueId), cueLabel: str(s.cueLabel) ?? '', cueName: str(s.cueName) ?? '' };
  else source = { kind: 'defaults', reason: s.reason === 'first_song' || s.reason === 'missing_cue' ? s.reason : 'chosen' };
  return { mode: asMode(r.mode), requestedMode: asMode(r.requestedMode ?? r.mode), source, values };
}

/** 每個機關的起始值：後台給的，沒有的在預設位置；沒有 start → null（舊模式，維持原樣） */
export function startValuesOf(mechs: readonly { id: string; defaultValue: number; travelMin: number; travelMax: number }[], start: SongStart | null | undefined): MechanismValues | null {
  if (!start) return null;
  const out: MechanismValues = {};
  for (const m of mechs) {
    const v = start.values[m.id];
    const raw = typeof v === 'number' ? v : m.defaultValue;
    out[m.id] = raw < m.travelMin ? m.travelMin : raw > m.travelMax ? m.travelMax : raw;
  }
  return out;
}

/** 時間軸開頭 key 旁邊寫的來源（例：「接上一首：S02」） */
export function startLabel(start: SongStart | null | undefined): string {
  if (!start) return '';
  const s = start.source;
  if (s.kind === 'prev_song') return `接上一首：${s.songCode || s.songName || '上一首'}`;
  if (s.kind === 'library_cue') return `指定 cue ${s.cueLabel}${s.cueName ? `「${s.cueName}」` : ''}`;
  if (s.reason === 'first_song') return '機關預設位置（第一首，沒有上一首）';
  if (s.reason === 'missing_cue') return '機關預設位置（指定的 cue 已經不在了）';
  return '機關預設位置';
}

/** 方向讀數 / 轉場說明裡的「上一個位置」（第一個 cue 的上一個 = 起始位置） */
export function startFromText(start: SongStart | null | undefined): string {
  return start ? `起始位置（${startLabel(start)}）` : '預設位置';
}

export const START_TEXT = {
  title: '這首歌從哪裡開始',
  button: (label: string) => `起始位置：${label}`,
  hint: '第一個 cue 會從這裡走過去，走法和其他 cue 一樣（轉場秒數、每片的延遲和走幾秒）。',
  prev: '接上一首的結尾',
  prevSub: '上一首最後一個 cue 的機關位置；第一首沒有上一首，就用機關預設位置',
  library: '指定一個 Cue 庫的 cue',
  librarySub: '用那個 cue 的機關位置當起點',
  defaults: '機關的預設位置',
  defaultsSub: '所有機關都從預設位置出發',
  pickCue: '選一個 cue',
  search: '找 cue（編號或名稱）',
  noCues: 'Cue 庫還是空的。',
  save: '設定',
  saved: (label: string) => `起始位置已設成：${label}`,
  missingNote: '原本指定的 cue 已經不在了，現在先接上一首的結尾。',
  moveNote: '第一個 cue 排在 0:00，往後挪才看得到轉過去（可以拖時間軸上的 cue 編號）。',
  readonly: '只有導演和 PM 可以改起始位置。',
} as const;
